import { useEffect, useRef } from "preact/hooks";

import { fetchStats } from "../lib/api";

/** Canvas-based sparkline of daily new jobs over the past 14 days.
 *
 * Fixes the legacy bug where the canvas stroke/fill was set to the
 * literal string 'var(--primary)' (which Canvas ignores). We now read
 * the actual computed value via getComputedStyle so the chart renders
 * in the theme color.
 */
export function Sparkline() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;

    async function draw() {
      const canvas = canvasRef.current;
      if (!canvas) return;

      let data: [string, number][] = [];
      try {
        const raw = await fetchStats();
        // StatsPoint is typed as { 0: string; 1: number } — coerce to tuples.
        data = raw.map((p) => [p[0], p[1]]);
      } catch (err) {
        console.error("Sparkline fetch error", err);
      }
      if (cancelled) return;

      // Read the theme color from CSS variables (the bug fix).
      const styles = getComputedStyle(document.documentElement);
      const primary = styles.getPropertyValue("--primary").trim() || "#eb9d22";
      const primaryAlpha = (a: number) => {
        // primary is a hex color; convert to rgba with alpha.
        const hex = primary.replace("#", "");
        if (hex.length === 6) {
          const r = parseInt(hex.slice(0, 2), 16);
          const g = parseInt(hex.slice(2, 4), 16);
          const b = parseInt(hex.slice(4, 6), 16);
          return `rgba(${r}, ${g}, ${b}, ${a})`;
        }
        return primary;
      };

      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, rect.width * dpr);
      canvas.height = Math.max(1, rect.height * dpr);
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const w = rect.width;
      const h = rect.height;
      ctx.clearRect(0, 0, w, h);

      // Build last-14-days series.
      const last14: string[] = [];
      for (let i = 13; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        last14.push(d.toISOString().split("T")[0]);
      }
      const map: Record<string, number> = {};
      for (const [date, count] of data) map[date] = count;
      const points = last14.map((d) => map[d] || 0);
      const maxVal = Math.max(...points, 3);
      const step = w / Math.max(1, points.length - 1);

      // Grid lines.
      ctx.strokeStyle = "rgba(255, 255, 255, 0.05)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, h * 0.25);
      ctx.lineTo(w, h * 0.25);
      ctx.moveTo(0, h * 0.75);
      ctx.lineTo(w, h * 0.75);
      ctx.stroke();

      // Area fill.
      ctx.beginPath();
      ctx.moveTo(0, h);
      points.forEach((p, idx) => {
        const x = idx * step;
        const y = h - (p / maxVal) * (h - 10) - 5;
        ctx.lineTo(x, y);
      });
      ctx.lineTo(w, h);
      ctx.closePath();
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, primaryAlpha(0.18));
      grad.addColorStop(1, primaryAlpha(0));
      ctx.fillStyle = grad;
      ctx.fill();

      // Line.
      ctx.beginPath();
      points.forEach((p, idx) => {
        const x = idx * step;
        const y = h - (p / maxVal) * (h - 10) - 5;
        if (idx === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = primary;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Peak dots.
      points.forEach((p, idx) => {
        if (p > 0) {
          const x = idx * step;
          const y = h - (p / maxVal) * (h - 10) - 5;
          ctx.beginPath();
          ctx.arc(x, y, 3, 0, Math.PI * 2);
          ctx.fillStyle = "#ffffff";
          ctx.fill();
          ctx.lineWidth = 1;
          ctx.strokeStyle = primary;
          ctx.stroke();
        }
      });
    }

    draw();
    // Re-render on resize.
    window.addEventListener("resize", draw);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", draw);
    };
  }, []);

  return (
    <div class="sparkline-wrap">
      <canvas ref={canvasRef} class="sparkline-canvas" aria-label="Daily new jobs over the past 14 days" role="img" />
    </div>
  );
}
